/**
 * DixonColesFilters - Filter controls for the Dixon-Coles page
 */

import React, { useState, useRef, useEffect } from 'react';
import type { DCFilters, BookmakerOption } from '../../types';
import { DCFilterPresets } from './DCFilterPresets';
import { createDCFilterPreset, updateDCFilterPreset, useDCFilterPresets } from '../../swr-config';
import type { DCFilterPreset } from '../../types';
import { LEAGUES_BY_COUNTRY, COUNTRY_FLAGS } from '../../context/LeagueContext';

interface Props {
  filters: DCFilters;
  onChange: (filters: Partial<DCFilters>) => void;
  onLoadPreset: (filters: DCFilters) => void;
  onClearFilters: () => void;
  bookmakers: BookmakerOption[];
  userPreferredSlug?: string | null;
  shouldFetchPresets?: boolean;
}

const DAYS_OPTIONS = [1, 3, 7, 14];

type MarketKey = 'home' | 'draw' | 'away' | 'over_1_5' | 'over_2_5' | 'over_3_5' | 'btts';

const MARKET_OPTIONS: { key: MarketKey; label: string; probKey: keyof DCFilters; minOddsKey: keyof DCFilters; maxOddsKey: keyof DCFilters }[] = [
  { key: 'home', label: 'Home Win', probKey: 'min_home_prob', minOddsKey: 'min_home_odds', maxOddsKey: 'max_home_odds' },
  { key: 'draw', label: 'Draw', probKey: 'min_draw_prob', minOddsKey: 'min_draw_odds', maxOddsKey: 'max_draw_odds' },
  { key: 'away', label: 'Away Win', probKey: 'min_away_prob', minOddsKey: 'min_away_odds', maxOddsKey: 'max_away_odds' },
  { key: 'over_1_5', label: 'Over 1.5', probKey: 'min_over_1_5', minOddsKey: 'min_over_1_5_odds', maxOddsKey: 'max_over_1_5_odds' },
  { key: 'over_2_5', label: 'Over 2.5', probKey: 'min_over_2_5', minOddsKey: 'min_over_2_5_odds', maxOddsKey: 'max_over_2_5_odds' },
  { key: 'over_3_5', label: 'Over 3.5', probKey: 'min_over_3_5', minOddsKey: 'min_over_3_5_odds', maxOddsKey: 'max_over_3_5_odds' },
  { key: 'btts', label: 'BTTS', probKey: 'min_btts', minOddsKey: 'min_btts_odds', maxOddsKey: 'max_btts_odds' },
];

export function DixonColesFilters({ filters, onChange, onLoadPreset, onClearFilters, bookmakers, userPreferredSlug, shouldFetchPresets = false }: Props) {
  const [showMarketPicker, setShowMarketPicker] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
  const [showLeaguePicker, setShowLeaguePicker] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [existingPresetToUpdate, setExistingPresetToUpdate] = useState<DCFilterPreset | null>(null);
  const saveDialogRef = useRef<HTMLDivElement>(null);
  const leaguePickerRef = useRef<HTMLDivElement>(null);
  const { presets, mutate: refreshPresets } = useDCFilterPresets(shouldFetchPresets);

  // Close save dialog when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (saveDialogRef.current && !saveDialogRef.current.contains(event.target as Node)) {
        setShowSavePresetDialog(false);
        setPresetName('');
        setSaveError(null);
        setExistingPresetToUpdate(null);
      }
    }
    if (showSavePresetDialog) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSavePresetDialog]);

  // Close league picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (leaguePickerRef.current && !leaguePickerRef.current.contains(event.target as Node)) {
        setShowLeaguePicker(false);
      }
    }
    if (showLeaguePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLeaguePicker]);

  // Track which market rows are visible (even if no values are set yet)
  const [visibleMarkets, setVisibleMarkets] = useState<Set<MarketKey>>(() => {
    // Initialize from existing filter values
    const initial = new Set<MarketKey>();
    MARKET_OPTIONS.forEach(opt => {
      if (filters[opt.probKey] !== undefined ||
          filters[opt.minOddsKey] !== undefined ||
          filters[opt.maxOddsKey] !== undefined) {
        initial.add(opt.key);
      }
    });
    return initial;
  });

  // Sync visibleMarkets when filters change (e.g., preset loaded or cleared)
  useEffect(() => {
    const marketsWithFilters = new Set<MarketKey>();
    MARKET_OPTIONS.forEach(opt => {
      if (filters[opt.probKey] !== undefined ||
          filters[opt.minOddsKey] !== undefined ||
          filters[opt.maxOddsKey] !== undefined) {
        marketsWithFilters.add(opt.key);
      }
    });

    // If filters were cleared, clear visible markets
    if (marketsWithFilters.size === 0 && visibleMarkets.size > 0) {
      setVisibleMarkets(new Set());
    }
    // If filters were loaded (e.g., from preset), show those markets
    else if (marketsWithFilters.size > 0) {
      // Check if there are markets in filters that aren't visible
      const needsUpdate = Array.from(marketsWithFilters).some(m => !visibleMarkets.has(m));
      if (needsUpdate) {
        setVisibleMarkets(marketsWithFilters);
      }
    }
  }, [filters]);

  // Get active market filters based on visibility state
  const activeMarkets = MARKET_OPTIONS.filter(opt => visibleMarkets.has(opt.key));

  // Count total active filters (only count actual values, not just visible rows)
  const activeFilterCount = MARKET_OPTIONS.reduce((count, market) => {
    let c = count;
    if (filters[market.probKey] !== undefined) c++;
    if (filters[market.minOddsKey] !== undefined) c++;
    if (filters[market.maxOddsKey] !== undefined) c++;
    return c;
  }, 0) + (filters.min_edge !== undefined ? 1 : 0);

  const hasActiveAdvancedFilters = activeFilterCount > 0 || visibleMarkets.size > 0;

  const handleAddMarket = (key: MarketKey) => {
    setVisibleMarkets(prev => new Set(prev).add(key));
    setShowMarketPicker(false);
  };

  const handleRemoveMarket = (market: typeof MARKET_OPTIONS[0]) => {
    // Remove from visible markets
    setVisibleMarkets(prev => {
      const next = new Set(prev);
      next.delete(market.key);
      return next;
    });
    // Clear any filter values
    onChange({
      [market.probKey]: undefined,
      [market.minOddsKey]: undefined,
      [market.maxOddsKey]: undefined,
    });
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      setSaveError('Please enter a name');
      return;
    }

    // Check if a preset with this name already exists
    const existingPreset = presets.find(
      p => p.name.toLowerCase() === presetName.trim().toLowerCase()
    );

    if (existingPreset && !existingPresetToUpdate) {
      // Show confirmation to update existing preset
      setExistingPresetToUpdate(existingPreset);
      return;
    }

    setSavingPreset(true);
    setSaveError(null);

    try {
      if (existingPresetToUpdate) {
        // Update existing preset
        await updateDCFilterPreset(existingPresetToUpdate.id, { filters });
      } else {
        // Create new preset
        await createDCFilterPreset(presetName.trim(), filters);
      }
      await refreshPresets();
      setShowSavePresetDialog(false);
      setPresetName('');
      setExistingPresetToUpdate(null);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const handleCancelUpdate = () => {
    setExistingPresetToUpdate(null);
  };

  const handleClearAllFilters = () => {
    // Clear all market filter values
    const clearUpdates: Partial<DCFilters> = { min_edge: undefined };
    MARKET_OPTIONS.forEach(opt => {
      clearUpdates[opt.probKey] = undefined;
      clearUpdates[opt.minOddsKey] = undefined;
      clearUpdates[opt.maxOddsKey] = undefined;
    });
    onChange(clearUpdates);
    // Clear visible markets
    setVisibleMarkets(new Set());
  };

  return (
    <div className="dc-filters">
      {/* Top row: Days, Bookmaker */}
      <div className="filters-row">
        <div className="filter-group">
          <span className="filter-label">Days ahead:</span>
          <div className="chip-group">
            {DAYS_OPTIONS.map(d => (
              <button
                key={d}
                className={`chip ${filters.days_ahead === d ? 'active' : ''}`}
                onClick={() => onChange({ days_ahead: d })}
                type="button"
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <span className="filter-label">Bookmaker:</span>
          <select
            className="bookmaker-select"
            value={filters.bookmaker_slug || ''}
            onChange={(e) => onChange({
              bookmaker_slug: e.target.value || null
            })}
          >
            <option value="">
              {userPreferredSlug ? 'My Preferred' : 'Default (Bet365)'}
            </option>
            <option value="best">Best Odds</option>
            {bookmakers.map(b => (
              <option key={b.slug} value={b.slug}>{b.name}</option>
            ))}
          </select>
        </div>

        {/* League filter */}
        <div className="filter-group league-filter-group" ref={leaguePickerRef}>
          <span className="filter-label">Leagues:</span>
          <button
            className={`league-filter-btn ${filters.league_ids && filters.league_ids.length > 0 ? 'has-selection' : ''}`}
            onClick={() => setShowLeaguePicker(!showLeaguePicker)}
            type="button"
          >
            {filters.league_ids && filters.league_ids.length > 0
              ? `${filters.league_ids.length} selected`
              : 'All Leagues'}
            <svg
              className={`toggle-chevron ${showLeaguePicker ? 'expanded' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="currentColor"
            >
              <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
            </svg>
          </button>

          {showLeaguePicker && (
            <div className="league-picker-dropdown">
              <div className="league-picker-header">
                <button
                  type="button"
                  className="league-select-all"
                  onClick={() => onChange({ league_ids: undefined })}
                >
                  Clear All
                </button>
              </div>
              <div className="league-picker-list">
                {Object.entries(LEAGUES_BY_COUNTRY).map(([country, countryLeagues]) => (
                  <div key={country} className="league-country-group">
                    <div className="league-country-header">
                      {COUNTRY_FLAGS[country] || ''} {country}
                    </div>
                    {countryLeagues.map(league => {
                      const isSelected = filters.league_ids?.includes(league.api_league_id);
                      return (
                        <label key={league.id} className="league-checkbox-item">
                          <input
                            type="checkbox"
                            checked={isSelected || false}
                            onChange={(e) => {
                              const currentIds = filters.league_ids || [];
                              if (e.target.checked) {
                                onChange({ league_ids: [...currentIds, league.api_league_id] });
                              } else {
                                const newIds = currentIds.filter(id => id !== league.api_league_id);
                                onChange({ league_ids: newIds.length > 0 ? newIds : undefined });
                              }
                            }}
                          />
                          <span className="league-name">{league.name}</span>
                          {league.type === 'cup' && <span className="league-type-badge">Cup</span>}
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Presets dropdown */}
        <DCFilterPresets
          currentFilters={filters}
          onLoadPreset={onLoadPreset}
          shouldFetchPresets={shouldFetchPresets}
        />

        {/* Clear preset button */}
        {hasActiveAdvancedFilters && (
          <button
            className="dc-clear-preset-btn"
            onClick={onClearFilters}
            type="button"
            title="Clear preset and reset filters"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M4 4l6 6M10 4l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="btn-text">Clear Preset</span>
          </button>
        )}

        {/* Advanced filters toggle */}
        <button
          className={`advanced-filters-toggle ${showAdvancedFilters ? 'active' : ''} ${hasActiveAdvancedFilters ? 'has-filters' : ''}`}
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1 3h14M3 8h10M5 13h6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
          </svg>
          Filters
          {hasActiveAdvancedFilters && (
            <span className="filter-count">{activeFilterCount}</span>
          )}
          <svg
            className={`toggle-chevron ${showAdvancedFilters ? 'expanded' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="currentColor"
          >
            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </button>
      </div>

      {/* Advanced filters (collapsible) */}
      {showAdvancedFilters && (
        <div className="advanced-filters-panel">
          {/* Market filters table */}
          <div className="market-filters-section">
            <div className="market-filters-header">
              <span className="market-col-label">Market</span>
              <span className="market-col-label">Min Prob %</span>
              <span className="market-col-label">Odds Range</span>
              <span className="market-col-label"></span>
            </div>

            {activeMarkets.map(market => (
              <div key={market.key} className="market-filter-row">
                <span className="market-name">{market.label}</span>

                <div className="market-input-group">
                  <span className="market-input-label">Min Prob %</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="5"
                    placeholder="—"
                    value={filters[market.probKey] !== undefined ? Math.round((filters[market.probKey] as number) * 100) : ''}
                    onChange={(e) => onChange({
                      [market.probKey]: e.target.value ? parseFloat(e.target.value) / 100 : undefined
                    })}
                    className="market-input"
                  />
                </div>

                <div className="market-odds-group">
                  <div className="market-odds-field">
                    <span className="market-input-label">Min Odds</span>
                    <input
                      type="number"
                      min="1"
                      step="0.1"
                      placeholder="—"
                      value={(filters[market.minOddsKey] as number | undefined) ?? ''}
                      onChange={(e) => onChange({
                        [market.minOddsKey]: e.target.value ? parseFloat(e.target.value) : undefined
                      })}
                      className="market-input odds"
                    />
                  </div>
                  <span className="odds-separator">-</span>
                  <div className="market-odds-field">
                    <span className="market-input-label">Max Odds</span>
                    <input
                      type="number"
                      min="1"
                      step="0.1"
                      placeholder="—"
                      value={(filters[market.maxOddsKey] as number | undefined) ?? ''}
                      onChange={(e) => onChange({
                        [market.maxOddsKey]: e.target.value ? parseFloat(e.target.value) : undefined
                      })}
                      className="market-input odds"
                    />
                  </div>
                </div>

                <button
                  className="market-remove-btn"
                  onClick={() => handleRemoveMarket(market)}
                  type="button"
                  aria-label={`Remove ${market.label} filter`}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <path d="M4 4l6 6M10 4l-6 6" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                </button>
              </div>
            ))}

            {/* Add market button, Save to Presets, and Clear All */}
            <div className="market-actions-row">
              <div className="add-market-wrapper">
                <button
                  className="add-market-btn"
                  onClick={() => setShowMarketPicker(!showMarketPicker)}
                  type="button"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                  Add Market Filter
                </button>

                {showMarketPicker && (
                  <div className="filter-picker-dropdown">
                    {MARKET_OPTIONS
                      .filter(opt => !visibleMarkets.has(opt.key))
                      .map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => handleAddMarket(opt.key)}
                          type="button"
                        >
                          {opt.label}
                        </button>
                      ))}
                    {MARKET_OPTIONS.filter(opt => !visibleMarkets.has(opt.key)).length === 0 && (
                      <span className="no-filters">All markets added</span>
                    )}
                  </div>
                )}
              </div>

              {/* Save to Presets button */}
              <div className="save-preset-wrapper" ref={saveDialogRef}>
                <button
                  className={`save-preset-btn ${activeFilterCount > 0 ? 'active' : ''}`}
                  onClick={() => setShowSavePresetDialog(!showSavePresetDialog)}
                  type="button"
                  disabled={activeFilterCount === 0}
                  title={activeFilterCount === 0 ? 'Add filters first' : 'Save current filters as a preset'}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M11 1H3a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2V4l-2-3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 1v3H5V1M5 8h4M5 11h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Save to Presets
                </button>

                {showSavePresetDialog && (
                  <div className="save-preset-dialog">
                    {saveError && (
                      <div className="save-preset-error">{saveError}</div>
                    )}
                    {existingPresetToUpdate ? (
                      <>
                        <div className="save-preset-confirm-msg">
                          A preset named "<strong>{existingPresetToUpdate.name}</strong>" already exists. Update it with current filters?
                        </div>
                        <div className="save-preset-actions">
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={handleCancelUpdate}
                            disabled={savingPreset}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn-save"
                            onClick={handleSavePreset}
                            disabled={savingPreset}
                          >
                            {savingPreset ? 'Updating...' : 'Update'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          placeholder="Preset name..."
                          value={presetName}
                          onChange={(e) => setPresetName(e.target.value)}
                          maxLength={50}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSavePreset();
                            if (e.key === 'Escape') {
                              setShowSavePresetDialog(false);
                              setPresetName('');
                              setSaveError(null);
                            }
                          }}
                        />
                        <div className="save-preset-actions">
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => {
                              setShowSavePresetDialog(false);
                              setPresetName('');
                              setSaveError(null);
                            }}
                            disabled={savingPreset}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn-save"
                            onClick={handleSavePreset}
                            disabled={savingPreset || !presetName.trim()}
                          >
                            {savingPreset ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Clear All button */}
              {activeFilterCount > 0 && (
                <button
                  className="clear-filters-btn"
                  onClick={handleClearAllFilters}
                  type="button"
                  title="Clear all filters"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M4 4l6 6M10 4l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Min Edge filter */}
          <div className="filters-section edge-section">
            <div className="filter-group">
              <span className="filter-section-label">Min Edge:</span>
              <input
                type="number"
                placeholder="e.g. 5"
                step="1"
                value={filters.min_edge ?? ''}
                onChange={(e) => onChange({
                  min_edge: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                className="edge-input"
              />
              <span>%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
